import { createEffect, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { msg } from "@lingui/core/macro";

import {
  checkDueForegroundReminders,
  FOREGROUND_FIRE_WINDOW_MIN,
  type HabitReminderTarget,
  MAX_SCHEDULED_REMINDERS,
  syncReminders,
} from "@habits/shared";
import { useHabitsQuery, useRemindersQuery } from "@habits/shared/solid";

import { usePublicConfig } from "@/features/app-config/use-public-config";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import {
  getNotificationAdapter,
  type NotificationAdapter,
  type ReminderContent,
} from "@/lib/notifications/service";
import { useI18n } from "@/providers/i18n-provider";

const FOREGROUND_POLL_MS = 30_000;

// `t` is received from the hook (running inside the provider's reactive tree)
// so that translation remains reactive. Passed explicitly because this function
// is imperative (constructs notification content at the time of the
// event) and does not render JSX.
function reminderContent(
  target: HabitReminderTarget,
  t: (msg: Parameters<ReturnType<typeof useI18n>["t"]>[0]) => string,
): ReminderContent {
  return {
    title: t(msg`Habit reminder`),
    body: target.habitName || t(msg`Your habit is waiting for today's check`),
  };
}

/**
 * Keeps habit reminders synchronized with platform:
 *   - Tauri (desktop + mobile): registers next occurrence of each
 *     enabled reminder with the OS scheduler.
 *   - Web / PWA: foreground polling; notifies when crossing time of day
 *     while a tab remains open.
 *
 * Mount only once (see `ReminderScheduler`), inside PowerSync provider.
 */
export function useHabitReminders(): void {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();

  const [adapter, setAdapter] = createSignal<NotificationAdapter | null>(null);
  const notifiedKeys = new Set<string>();
  let syncing = false;

  // CONFIG_OVER_CODE: reminder caps are resolved by the server
  // (code default → env → admin override → clamp). Without response uses
  // local constant, which IS the code default: historical behavior.
  const publicConfig = usePublicConfig();
  const maxScheduled = (): number =>
    publicConfig()?.reminders?.maxScheduled ?? MAX_SCHEDULED_REMINDERS;
  const foregroundWindowMin = (): number =>
    publicConfig()?.reminders?.foregroundFireWindowMin ?? FOREGROUND_FIRE_WINDOW_MIN;

  onMount(() => {
    void (async () => {
      const resolved = await getNotificationAdapter();
      setAdapter(resolved);
      // Best-effort early prompt; on web only prompts with prior activation.
      await resolved.requestPermission();
    })();
  });

  const reminders = useRemindersQuery({
    db,
    drizzle,
    userId: session.userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const habits = useHabitsQuery({
    db,
    drizzle,
    userId: session.userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const targets = createMemo<HabitReminderTarget[]>(() => {
    const habitNames = new Map((habits.data ?? []).map((h) => [h.id, h.name ?? ""]));
    return (reminders.data ?? [])
      .filter((r) => r.enabled === 1 && habitNames.has(r.habit_id ?? ""))
      .map((r) => ({
        id: r.id,
        habitId: r.habit_id ?? "",
        habitName: habitNames.get(r.habit_id ?? "") ?? "",
        timeOfDay: r.time_of_day ?? "09:00",
      }));
  });

  const syncScheduled = async (resolved: NotificationAdapter, list: HabitReminderTarget[]) => {
    if (syncing) {
      return;
    }
    syncing = true;
    try {
      if (!(await resolved.requestPermission())) {
        return;
      }
      const currentlyScheduled = await resolved.getScheduled();
      await syncReminders({
        targets: list,
        currentlyScheduled,
        schedule: async (target) => resolved.schedule(target, reminderContent(target, t)),
        cancel: async (id) => resolved.cancel(id),
        maxNotifications: maxScheduled(),
      });
    } catch (error) {
      console.error("[notifications] Failed to sync reminders:", error);
    } finally {
      syncing = false;
    }
  };

  const checkForeground = (resolved: NotificationAdapter, list: HabitReminderTarget[]) => {
    checkDueForegroundReminders({
      targets: list,
      notifiedKeys,
      windowMin: foregroundWindowMin(),
      onNotify: (target) => {
        const content = reminderContent(target, t);
        void resolved.show(content.title, {
          body: content.body,
          data: { reminderId: target.id },
        });
      },
    });
  };

  // Reacts to data changes: Tauri re-syncs OS; web re-checks.
  createEffect(() => {
    const resolved = adapter();
    const list = targets();
    if (!resolved) {
      return;
    }
    if (resolved.supportsScheduling) {
      void syncScheduled(resolved, list);
    } else {
      checkForeground(resolved, list);
    }
  });

  // Foreground heartbeat for web path.
  onMount(() => {
    const interval = setInterval(() => {
      const resolved = adapter();
      if (resolved && !resolved.supportsScheduling) {
        checkForeground(resolved, targets());
      }
    }, FOREGROUND_POLL_MS);
    onCleanup(() => {
      clearInterval(interval);
    });
  });
}
